import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Check, LoaderCircle, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { EASE } from "@/components/landing/Reveal";
import { CourriersSection } from "@/components/dashboard/Courriers";

export default function MesDocuments() {
  const [docs, setDocs] = useState(null);
  const [label, setLabel] = useState("");
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    api.get("/documents").then((r) => setDocs(r.data)).catch(() => setDocs(false));
  }, []);

  const groups = useMemo(() => {
    if (!Array.isArray(docs)) return [];
    const map = {};
    docs.forEach((d) => {
      (map[d.category] = map[d.category] || []).push(d);
    });
    return Object.entries(map);
  }, [docs]);

  const checked = Array.isArray(docs) ? docs.filter((d) => d.checked).length : 0;

  const toggle = async (doc) => {
    setDocs((ds) => ds.map((d) => (d.id === doc.id ? { ...d, checked: !d.checked } : d)));
    try {
      await api.patch(`/documents/${doc.id}`, { checked: !doc.checked });
    } catch {
      setDocs((ds) => ds.map((d) => (d.id === doc.id ? { ...d, checked: doc.checked } : d)));
      toast.error("Impossible de mettre à jour ce document.");
    }
  };

  const addDoc = async (e) => {
    e.preventDefault();
    if (!label.trim()) return;
    setAdding(true);
    try {
      const { data } = await api.post("/documents", { label: label.trim(), category: "Autre" });
      setDocs((ds) => [...ds, data]);
      setLabel("");
      toast.success("Document ajouté à votre checklist.");
    } catch {
      toast.error("Impossible d'ajouter ce document.");
    } finally {
      setAdding(false);
    }
  };

  const remove = async (doc) => {
    try {
      await api.delete(`/documents/${doc.id}`);
      setDocs((ds) => ds.filter((d) => d.id !== doc.id));
      toast.success("Document supprimé.");
    } catch {
      toast.error("Impossible de supprimer ce document.");
    }
  };

  if (docs === null)
    return <div className="flex justify-center py-24"><LoaderCircle className="h-7 w-7 animate-spin text-bleu" /></div>;
  if (docs === false)
    return <p className="py-24 text-center text-sm text-slate-500">Impossible de charger vos documents.</p>;

  return (
    <div className="mx-auto max-w-3xl space-y-6" data-testid="page-mes-documents">
      <div>
        <h1 className="font-display text-2xl font-extrabold text-slate-900 sm:text-3xl">Mes documents</h1>
        <p className="mt-1 text-sm text-slate-500">Cochez les pièces justificatives déjà réunies pour votre dossier.</p>
      </div>

      <div className="card-integra p-5" data-testid="documents-progress-card">
        <div className="flex items-center justify-between">
          <p className="text-sm font-bold text-slate-900">Pièces validées</p>
          <p className="text-sm font-extrabold text-emeraude" data-testid="documents-progress-value">{checked}/{docs.length}</p>
        </div>
        <div className="mt-3 h-2.5 overflow-hidden rounded-full bg-slate-100">
          <div className="h-full rounded-full bg-emeraude transition-all duration-700" style={{ width: `${docs.length ? (checked / docs.length) * 100 : 0}%` }} />
        </div>
      </div>

      <form onSubmit={addDoc} className="flex gap-2.5" data-testid="documents-add-form">
        <input
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="Ajouter un document (ex : attestation de travail)"
          className="flex-1 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm placeholder:text-slate-400 focus:border-bleu focus:outline-none focus:ring-2 focus:ring-bleu/15"
          data-testid="documents-add-input"
        />
        <button type="submit" disabled={adding || !label.trim()} className="btn-primary !px-5 !py-3 disabled:opacity-50" data-testid="documents-add-submit">
          <Plus className="h-4 w-4" /> Ajouter
        </button>
      </form>

      {groups.map(([category, items], gi) => (
        <motion.section
          key={category}
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: gi * 0.06, ease: EASE }}
          data-testid={`documents-group-${category}`}
        >
          <h2 className="mb-2.5 text-xs font-bold uppercase tracking-wider text-slate-400">{category}</h2>
          <ul className="space-y-2">
            {items.map((doc) => (
              <li key={doc.id} className={`card-integra flex items-center gap-3.5 p-4 transition-colors ${doc.checked ? "bg-emerald-50/40" : ""}`}>
                <button
                  onClick={() => toggle(doc)}
                  aria-label={doc.checked ? "Décocher" : "Cocher"}
                  className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-lg border-2 transition-all ${
                    doc.checked ? "border-emeraude bg-emeraude text-white" : "border-slate-300 bg-white text-transparent hover:border-emeraude"
                  }`}
                  data-testid={`document-toggle-${doc.id}`}
                >
                  <Check className="h-3.5 w-3.5" />
                </button>
                <span className={`flex-1 text-sm font-medium ${doc.checked ? "text-slate-400 line-through" : "text-slate-800"}`}>{doc.label}</span>
                {doc.custom && (
                  <button onClick={() => remove(doc)} className="text-slate-300 transition-colors hover:text-rouge" aria-label="Supprimer" data-testid={`document-delete-${doc.id}`}>
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </li>
            ))}
          </ul>
        </motion.section>
      ))}

      <CourriersSection />
    </div>
  );
}
